import React, { useEffect, useState, useCallback, useMemo } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useAgencyWS } from "@/hooks/useAgencyWS";
import { useNetworkStatus } from "@/hooks/useNetworkStatus";
import { warnDev } from "@/lib/log";
import { toast } from "sonner";
import { CounterHeader } from "@/components/counter/CounterHeader";
import { ActiveTicketPanel } from "@/components/counter/ActiveTicketPanel";
import { WaitingQueueByCategory } from "@/components/counter/WaitingQueueByCategory";
import { CounterStats } from "@/components/counter/CounterStats";
import { TransferDialog } from "@/components/counter/TransferDialog";
import { CancelDialog } from "@/components/counter/CancelDialog";

const CANCEL_ROLES = new Set(["SUPER_ADMIN", "AGENCY_ADMIN", "RECEPTIONIST"]);

export default function CounterPage() {
  const { user, logout } = useAuth();
  const [counters, setCounters] = useState([]);
  const [categories, setCategories] = useState([]);
  const [counterId, setCounterId] = useState(() => localStorage.getItem("mama.counter_id") || "");
  const [activeTicket, setActiveTicket] = useState(null);
  const [waiting, setWaiting] = useState([]);
  const [transferOpen, setTransferOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const agencyId = user?.agency_id;
  const canCancel = CANCEL_ROLES.has(user?.role);
  const { online, backendReachable } = useNetworkStatus();

  /* Initial loads */
  useEffect(() => {
    if (!agencyId) return;
    api.get("/counters", { params: { agency_id: agencyId } })
      .then((r) => setCounters(r.data || []))
      .catch((e) => warnDev("counters fetch failed", e));
    api.get("/categories")
      .then((r) => setCategories(r.data || []))
      .catch((e) => warnDev("categories fetch failed", e));
  }, [agencyId]);

  /* Live queue refresh */
  const refresh = useCallback(async () => {
    if (!agencyId) return;
    try {
      const { data } = await api.get("/tickets/queue", { params: { agency_id: agencyId } });
      setWaiting(data.waiting || []);
      const mine = (data.serving || []).find(
        (t) => t.assigned_counter_id === counterId && (t.status === "called" || t.status === "serving"),
      );
      setActiveTicket(mine || null);
    } catch (e) {
      warnDev("counter refresh failed", e);
    }
  }, [agencyId, counterId]);

  useEffect(() => { refresh(); }, [refresh]);

  const onWs = useCallback((msg) => {
    if (["ticket.created", "ticket.update", "ticket.called", "ticket.recall"].includes(msg.type)) {
      refresh();
    }
  }, [refresh]);
  const wsStatus = useAgencyWS(agencyId, onWs);

  /* Handlers */
  const onSelectCounter = (id) => {
    setCounterId(id);
    localStorage.setItem("mama.counter_id", id);
  };

  const guardActive = (fn) => async (...args) => {
    if (!activeTicket) return;
    setBusy(true);
    try { await fn(...args); }
    finally { setBusy(false); }
  };

  const callNext = async () => {
    if (!counterId) { toast.error("Sélectionnez un guichet"); return; }
    if (activeTicket) {
      toast.error("Vous avez déjà un ticket actif. Terminez-le avant d'en appeler un autre.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/tickets/call-next", { counter_id: counterId });
      setActiveTicket(data);
      toast.success(`Ticket ${data.full_ticket_code} appelé`);
      refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Aucun ticket en attente");
    } finally {
      setBusy(false);
    }
  };

  const recall = guardActive(async () => {
    try {
      const { data } = await api.post(`/tickets/${activeTicket.id}/recall`);
      setActiveTicket(data);
      toast.info(`Rappel (${data.recall_count}) envoyé sur l'écran`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Erreur");
    }
  });

  const startServing = guardActive(async () => {
    try {
      const { data } = await api.post(`/tickets/${activeTicket.id}/start`);
      setActiveTicket(data);
      toast.success("Traitement démarré");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Erreur");
    }
  });

  const complete = guardActive(async () => {
    try {
      await api.post(`/tickets/${activeTicket.id}/complete`);
      toast.success("Ticket terminé");
      setActiveTicket(null);
      refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Erreur");
    }
  });

  const markMissed = guardActive(async () => {
    try {
      await api.post(`/tickets/${activeTicket.id}/missed`);
      toast.info("Ticket marqué comme absent");
      setActiveTicket(null);
      refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Erreur");
    }
  });

  const submitTransfer = async ({ mode, target, reason }) => {
    if (!activeTicket || !target) return;
    setBusy(true);
    try {
      const body = { reason };
      if (mode === "category") body.target_category_code = target;
      else body.target_counter_id = target;
      await api.post(`/tickets/${activeTicket.id}/transfer`, body);
      toast.success("Ticket transféré");
      setActiveTicket(null);
      setTransferOpen(false);
      refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Erreur");
    } finally {
      setBusy(false);
    }
  };

  const submitCancel = async (reason) => {
    if (!activeTicket) return;
    setBusy(true);
    try {
      await api.post(`/tickets/${activeTicket.id}/cancel`, { reason });
      toast.success("Ticket annulé");
      setActiveTicket(null);
      setCancelOpen(false);
      refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Erreur");
    } finally {
      setBusy(false);
    }
  };

  const selectedCounter = useMemo(
    () => counters.find((c) => c.id === counterId) || null,
    [counters, counterId],
  );
  const allowedCats = selectedCounter?.allowed_categories || [];

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <CounterHeader
        user={user}
        counters={counters}
        counterId={counterId}
        onSelectCounter={onSelectCounter}
        onLogout={logout}
        selectedCounter={selectedCounter}
        online={online}
        backendReachable={backendReachable}
        wsStatus={wsStatus}
      />

      <main className="flex-1 grid grid-cols-1 lg:grid-cols-5 gap-6 p-6">
        {/* Active ticket panel — 60% */}
        <section className="lg:col-span-3 bg-white border border-slate-200 rounded-2xl p-6 flex flex-col"
                 data-testid="active-ticket-section">
          <ActiveTicketPanel
            ticket={activeTicket}
            counterId={counterId}
            counterName={selectedCounter?.counter_name || "—"}
            canCancel={canCancel}
            hasAllowedCats={allowedCats.length > 0}
            onCallNext={callNext}
            onRecall={recall}
            onStart={startServing}
            onComplete={complete}
            onMissed={markMissed}
            onTransfer={() => setTransferOpen(true)}
            onCancel={() => setCancelOpen(true)}
            busy={busy}
          />
        </section>

        {/* Right column — 40% : stats + queue */}
        <aside className="lg:col-span-2 flex flex-col gap-4">
          <CounterStats agencyId={agencyId} />

          <div className="bg-white border border-slate-200 rounded-2xl p-5 flex-1 flex flex-col min-h-0">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-display font-bold text-lg text-slate-900">File par catégorie</h2>
              <div className="text-xs text-slate-500" data-testid="waiting-count">{waiting.length} ticket(s)</div>
            </div>
            <WaitingQueueByCategory
              waiting={waiting}
              allowedCategoryCodes={allowedCats}
            />
          </div>
        </aside>
      </main>

      <TransferDialog
        open={transferOpen}
        onOpenChange={setTransferOpen}
        ticket={activeTicket}
        categories={categories}
        counters={counters}
        currentCounterId={counterId}
        onConfirm={submitTransfer}
        busy={busy}
      />

      <CancelDialog
        open={cancelOpen}
        onOpenChange={setCancelOpen}
        ticket={activeTicket}
        onConfirm={submitCancel}
        busy={busy}
      />
    </div>
  );
}
